/**
 * Générateur — Associer et utiliser différentes écritures d'un nombre décimal
 * Attendu ID : 601 | Niveau : 6ème | Domaine : NC
 *
 * Types générés : qcm_unique, qcm_multiple, texte, texte_trou_drag,
 *                 texte_trou_saisie, relier, classer, tableau_vf
 * Labels de référence :
 *   - Donner l'écriture décimale puis le pourcentage d'une fraction
 *   - Convertir une fraction en écriture décimale et pourcentage
 *   - Classer des nombres égaux à un pourcentage donné
 *   - Trouver l'intrus parmi plusieurs écritures d'un même nombre
 *   - Écrire une fraction comme nombre mixte
 *   - Calculer avec des fractions décimales et donner le résultat en trois écritures
 * Variantes estimées : >500
 */

import {
    randomChoice, randomChoiceMultiple, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier, makeClasser,
    tex, fracTex,
} from '../../../utils';

// ─── Constantes ───
const ATTENDU_ID = 601;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Pools de données ───

/** Fractions convertibles en décimal avec leurs 4 écritures (% ≤ 100) */
const ECRITURES = [
    { frac: '2/10', num: 2, den: 10, dec: '0,2', pct: 20 },
    { frac: '7/10', num: 7, den: 10, dec: '0,7', pct: 70 },
    { frac: '3/10', num: 3, den: 10, dec: '0,3', pct: 30 },
    { frac: '9/10', num: 9, den: 10, dec: '0,9', pct: 90 },
    { frac: '7/100', num: 7, den: 100, dec: '0,07', pct: 7 },
    { frac: '25/100', num: 25, den: 100, dec: '0,25', pct: 25 },
    { frac: '50/100', num: 50, den: 100, dec: '0,5', pct: 50 },
    { frac: '75/100', num: 75, den: 100, dec: '0,75', pct: 75 },
    { frac: '3/1000', num: 3, den: 1000, dec: '0,003', pct: 0.3 },
    { frac: '17/1000', num: 17, den: 1000, dec: '0,017', pct: 1.7 },
    { frac: '1/2', num: 1, den: 2, dec: '0,5', pct: 50 },
    { frac: '1/4', num: 1, den: 4, dec: '0,25', pct: 25 },
    { frac: '3/4', num: 3, den: 4, dec: '0,75', pct: 75 },
    { frac: '1/5', num: 1, den: 5, dec: '0,2', pct: 20 },
    { frac: '2/5', num: 2, den: 5, dec: '0,4', pct: 40 },
    { frac: '3/5', num: 3, den: 5, dec: '0,6', pct: 60 },
    { frac: '4/5', num: 4, den: 5, dec: '0,8', pct: 80 },
    { frac: '7/20', num: 7, den: 20, dec: '0,35', pct: 35 },
    { frac: '9/20', num: 9, den: 20, dec: '0,45', pct: 45 },
];

/** Fractions > 1 pour les nombres mixtes (dénominateurs qui donnent des décimaux) */
const FRACTIONS_MIXTES = [
    { num: 6, den: 5, entier: 1, resteNum: 1, resteDen: 5, dec: '1,2' },
    { num: 9, den: 5, entier: 1, resteNum: 4, resteDen: 5, dec: '1,8' },
    { num: 7, den: 4, entier: 1, resteNum: 3, resteDen: 4, dec: '1,75' },
    { num: 9, den: 4, entier: 2, resteNum: 1, resteDen: 4, dec: '2,25' },
    { num: 11, den: 4, entier: 2, resteNum: 3, resteDen: 4, dec: '2,75' },
    { num: 9, den: 2, entier: 4, resteNum: 1, resteDen: 2, dec: '4,5' },
    { num: 7, den: 2, entier: 3, resteNum: 1, resteDen: 2, dec: '3,5' },
    { num: 13, den: 10, entier: 1, resteNum: 3, resteDen: 10, dec: '1,3' },
    { num: 27, den: 10, entier: 2, resteNum: 7, resteDen: 10, dec: '2,7' },
    { num: 11, den: 5, entier: 2, resteNum: 1, resteDen: 5, dec: '2,2' },
    { num: 14, den: 5, entier: 2, resteNum: 4, resteDen: 5, dec: '2,8' },
];

/** Fractions > 1 pour nombres mixtes (dénominateurs quelconques, pas forcément décimaux) */
const FRACTIONS_MIXTES_GENERAL = [
    { num: 21, den: 9, entier: 2, resteNum: 3, resteDen: 9 },
    { num: 15, den: 4, entier: 3, resteNum: 3, resteDen: 4 },
    { num: 22, den: 7, entier: 3, resteNum: 1, resteDen: 7 },
    { num: 10, den: 9, entier: 1, resteNum: 1, resteDen: 9 },
    { num: 25, den: 11, entier: 2, resteNum: 3, resteDen: 11 },
    { num: 36, den: 5, entier: 7, resteNum: 1, resteDen: 5 },
    { num: 17, den: 3, entier: 5, resteNum: 2, resteDen: 3 },
    { num: 19, den: 6, entier: 3, resteNum: 1, resteDen: 6 },
    { num: 23, den: 8, entier: 2, resteNum: 7, resteDen: 8 },
];


// ─── Helpers ───
const fracKatex = (num, den) => `\\frac{${num}}{${den}}`;
const exprElement = (contenu) => ({ type: 'expression', config: { contenu } });

// Fractions en KaTeX partout où l'élève les lit (audit fractions en texte,
// 2026-09-06). Le champ `frac` (« 1/4 ») des pools n'est plus affiché.
// En formule : virgule décimale `{,}`, pour cent `\%` (sinon commentaire LaTeX).
const decTex = (s) => String(s).replace(',', '{,}');
// Trous dans une fraction d'une ligne KaTeX globale (motif 620-622 : `\rule`
// invisibles pour la hauteur du slot, triple accolade décodée par preprocessBraces).
const RULE_NUM = '\\rule[-0.55em]{0pt}{0.55em}';
const RULE_DEN = '\\rule{0pt}{1.3em}';
const fracTrouNum = (num, den) => `\\dfrac{{{${num}}}${RULE_NUM}}{${den}}`;
const fracTrous = (num, den) => `\\dfrac{{{${num}}}${RULE_NUM}}{${RULE_DEN}{{${den}}}}`;

/**
 * Génère un SVG de rectangles coloriés pour visualiser une fraction > 1.
 * Ex: 21/9 → 2 rectangles pleins (9/9 coloriés) + 1 rectangle avec 3/9 coloriés.
 * @param {number} num - Numérateur
 * @param {number} den - Dénominateur
 * @returns {string} SVG inline en data URI
 */
const fractionRectsSvg = (num, den) => {
    const entier = Math.floor(num / den);
    const reste = num % den;
    const totalRects = reste > 0 ? entier + 1 : entier;
    const rectW = 60;
    const rectH = 40;
    const gap = 12;
    const svgW = totalRects * (rectW + gap) - gap + 20;
    const svgH = rectH + 20;

    let rects = '';
    for (let r = 0; r < totalRects; r++) {
        const x = 10 + r * (rectW + gap);
        const y = 10;
        const parts = r < entier ? den : reste; // nombre de parts coloriées
        // Fond du rectangle
        rects += `<rect x="${x}" y="${y}" width="${rectW}" height="${rectH}" fill="var(--svg-bg, white)" stroke="currentColor" stroke-width="1.5"/>`;
        // Parts coloriées
        const partW = rectW / den;
        for (let p = 0; p < parts; p++) {
            rects += `<rect x="${x + p * partW}" y="${y}" width="${partW}" height="${rectH}" fill="#ef4444" stroke="currentColor" stroke-width="0.5" opacity="0.7"/>`;
        }
        // Lignes verticales de séparation
        for (let p = 1; p < den; p++) {
            rects += `<line x1="${x + p * partW}" y1="${y}" x2="${x + p * partW}" y2="${y + rectH}" stroke="currentColor" stroke-width="0.5"/>`;
        }
    }

    return `data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${svgW} ${svgH}" width="${svgW}" height="${svgH}">${rects}</svg>`)}`;
};

// ─── Labels ───
const L3 = 'Classer des nombres égaux à un pourcentage donné';
const L4 = 'Trouver l\'intrus parmi plusieurs écritures d\'un même nombre';
const L5 = 'Écrire une fraction comme nombre mixte';
const L6 = 'Calculer avec des fractions décimales et donner le résultat en trois écritures';

// ═══════════════════════════════════════════════════════════
// EXERCICE 3 : Classer des nombres par pourcentage
// ═══════════════════════════════════════════════════════════

/** Classer — regrouper par valeur */
const genClasserParPct = () => {
    const groupes = randomChoiceMultiple(
        [
            { name: '10 %', items: ['0,1', fracTex(1, 10)] },
            { name: '25 %', items: ['0,25', fracTex(1, 4)] },
            { name: '50 %', items: ['0,5', fracTex(1, 2), fracTex(5, 10)] },
            { name: '20 %', items: ['0,2', fracTex(1, 5)] },
            { name: '75 %', items: ['0,75', fracTex(3, 4)] },
            { name: '80 %', items: ['0,8', fracTex(4, 5)] },
        ],
        3,
    );

    const categories = groupes.map(g => ({
        name: g.name,
        items: randomChoiceMultiple(g.items, Math.min(2, g.items.length)),
    }));

    return makeClasser(
        categories,
        '<p>Classe ces nombres selon le pourcentage auquel ils correspondent.</p>',
        opts(L3),
    );
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 4 : Trouver l'intrus
// ═══════════════════════════════════════════════════════════

/** QCM unique — "Trouve l'intrus" */
// ─── Moteur d'écritures équivalentes (enrichissement 2026-07-04) ───
// Une valeur propre (pourcentage entier) → ses écritures DÉRIVÉES mécaniquement,
// et des intrus fabriqués par MISCONCEPTION, différents par construction
// (×10, ÷10, inversion…). Remplace les groupes figés INTRUS_GROUPES (15 var.).

const PCT_PROPRES = [4, 5, 8, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55, 60, 65, 70, 75, 80, 85, 90, 95];

/** Nombres en lettres nécessaires (formes sûres, identiques en orthographe
 *  traditionnelle et rectifiée — pas de « et un » ni de cas ambigus). */
const MOTS_NOMBRES = {
    1: 'un', 2: 'deux', 3: 'trois', 4: 'quatre', 5: 'cinq', 6: 'six', 7: 'sept',
    8: 'huit', 9: 'neuf', 10: 'dix', 15: 'quinze', 20: 'vingt', 25: 'vingt-cinq',
    30: 'trente', 35: 'trente-cinq', 40: 'quarante', 45: 'quarante-cinq',
    50: 'cinquante', 55: 'cinquante-cinq', 60: 'soixante', 65: 'soixante-cinq',
    70: 'soixante-dix', 75: 'soixante-quinze', 80: 'quatre-vingts',
    85: 'quatre-vingt-cinq', 90: 'quatre-vingt-dix', 95: 'quatre-vingt-quinze',
};

const pgcd601 = (x, y) => (y === 0 ? x : pgcd601(y, x % y));
const majuscule601 = (s) => s.charAt(0).toUpperCase() + s.slice(1);

/** Dérive toutes les écritures d'un pourcentage entier propre. */
const deriveEcritures601 = (pct) => {
    const g = pgcd601(pct, 100);
    const num = pct / g;
    const den = 100 / g;
    const dec = String(pct / 100).replace('.', ',');
    // En mots : écriture célèbre (1 fois sur 2 si dispo), sinon dixièmes/centièmes
    const celebres = { 10: 'Un dixième', 20: 'Un cinquième', 25: 'Un quart', 50: 'Un demi', 75: 'Trois quarts' };
    let mots;
    if (celebres[pct] && Math.random() < 0.5) {
        mots = celebres[pct];
    } else if (pct % 10 === 0) {
        const d = pct / 10;
        mots = d === 1 ? 'Un dixième' : `${majuscule601(MOTS_NOMBRES[d])} dixièmes`;
    } else {
        mots = `${majuscule601(MOTS_NOMBRES[pct])} centièmes`;
    }
    return { pct, num, den, dec, frac: fracTex(num, den), pctStr: `${pct} %`, mots };
};

/**
 * Intrus candidats — chacun est DIFFÉRENT de la valeur par construction :
 *   (a) rang décalé en mots (dixièmes ↔ centièmes : ×10 ou ÷10)
 *   (b) décimal décalé d'un rang (÷10)
 *   (c) fraction inversée (> 1) ou dénominateur sur 100 (si num = 1)
 *   (d) pourcentage du dénominateur (confusion den ↔ %)
 */
const intrusCandidats601 = (e) => {
    const candidats = [];
    if (e.pct % 10 === 0) {
        candidats.push(`${majuscule601(MOTS_NOMBRES[e.pct / 10])} centièmes`); // ÷10
    } else {
        candidats.push(`${majuscule601(MOTS_NOMBRES[e.pct])} dixièmes`);        // ×10
    }
    candidats.push(String(e.pct / 1000).replace('.', ','));                       // ÷10 en décimal
    // Garde-fou : si den = pct (cas pct = 10, car 10² = 100), den/100 SERAIT
    // égal à la valeur cible → on bascule sur la fraction inversée
    candidats.push(e.num === 1 && e.den !== e.pct ? fracTex(e.den, 100) : fracTex(e.den, e.num));
    if (e.den !== e.pct) candidats.push(`${e.den} %`);
    return candidats;
};

const genIntrusQcm = () => {
    const e = deriveEcritures601(randomChoice(PCT_PROPRES));
    const corrects = [e.frac, e.dec, e.pctStr, e.mots];
    const intrus = randomChoice(intrusCandidats601(e));
    const choix = shuffle([...corrects, intrus]);

    return makeQcm(
        choix,
        intrus,
        '<p>Parmi ces écritures, trouve l\'intrus (celui qui n\'est pas égal aux autres).</p>',
        opts(L4),
    );
};

/** QCM multiple — "Quelles écritures sont égales à 0,2 ?" */
const genEcrituresEgalesQcmMulti = () => {
    const e = deriveEcritures601(randomChoice(PCT_PROPRES));

    // Bonnes réponses : 3 écritures dérivées (le pivot de la consigne est le
    // décimal, donc il n'apparaît pas dans les choix) ; la fraction sur 100
    // non simplifiée est aussi une écriture correcte.
    const corrects = randomChoiceMultiple(
        [e.frac, e.pctStr, e.mots, fracTex(e.pct, 100)],
        3,
    );

    // Distracteurs : 2 intrus de familles différentes (≠ valeur par construction)
    const distracteurs = randomChoiceMultiple(intrusCandidats601(e), 2);

    const choix = [...corrects, ...distracteurs];

    return makeQcm(
        choix,
        corrects,
        `<p>Parmi ces écritures, lesquelles sont égales à ${e.dec} ?</p>`,
        opts(L4),
        true, // multi
    );
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 5 : Nombre mixte
// ═══════════════════════════════════════════════════════════

/** Génère dynamiquement une fraction impropre num/den > 1 avec sa décomposition
 *  en nombre mixte (entier + reste/den). ~700 variantes. */
const genFractionMixteDynamique = () => {
    const den = randInt(3, 12);
    const entier = randInt(1, 6);
    const resteNum = randInt(1, den - 1); // reste < den
    const num = entier * den + resteNum;
    return { num, den, entier, resteNum, resteDen: den };
};

/** Texte à trous saisie — fraction = entier + fraction < 1, avec rectangles SVG */
const genNombreMixteSaisie = () => {
    const item = genFractionMixteDynamique();
    const svgUrl = fractionRectsSvg(item.num, item.den);
    // Ligne KaTeX globale : fraction verticale, l'entier en trou, puis la
    // fraction restante avec ses deux trous.
    const phrase = `$\\dfrac{${item.num}}{${item.den}} = {{${item.entier}}} + ${fracTrous(item.resteNum, item.resteDen)}$`;

    return makeTexteTrou(
        phrase,
        `<p>En t'aidant du dessin, écris la fraction sous la forme de la somme d'un nombre entier et d'une fraction plus petite que 1.</p>`,
        {
            ...opts(L5),
            mode: 'saisie',
            elements: { version: 1, elements: [{ type: 'image', config: { imageUrl: svgUrl } }] },
        },
    );
};

/** QCM unique — "Quelle est la décomposition de a/b ?" */
const genNombreMixteQcm = () => {
    // Même intervalle que la saisie : générateur dynamique PARTAGÉ
    // (den ∈ [3;12], entier ∈ [1;6] → 7 rectangles SVG max, déjà validé à
    // l'usage) — remplace les 9 items figés (enrichissement 2026-07-04).
    // num = entier × den + reste ⇒ la décomposition est vraie par construction.
    const item = genFractionMixteDynamique();

    const correct = {
        type: 'expression',
        config: { contenu: `${item.entier} + ${fracKatex(item.resteNum, item.resteDen)}` },
    };

    // Pièges — 4 familles, rotation de 3, exclusions par construction
    const famillesPieges = [
        // Numérateur au lieu du quotient
        `${item.num} + ${fracKatex(1, item.den)}`,
        // Mauvais quotient (entier + 1)
        `${item.entier + 1} + ${fracKatex(item.resteNum, item.den)}`,
        // Inversion num/den du reste (fraction ≥ 1 : détectable par la consigne)
        // — exclue si reste = 1 (donnerait une fraction sur 1, illisible)
        ...(item.resteNum > 1 ? [`${item.entier} + ${fracKatex(item.resteDen, item.resteNum)}`] : []),
        // Complément : compter les parts VIDES du dessin (den − reste)
        // — exclu si den − reste = reste (serait ÉGAL à la bonne réponse)
        ...(item.den - item.resteNum !== item.resteNum
            ? [`${item.entier} + ${fracKatex(item.den - item.resteNum, item.den)}`]
            : []),
    ];
    const distracteurs = randomChoiceMultiple(famillesPieges, Math.min(3, famillesPieges.length))
        .map(contenu => ({ type: 'expression', config: { contenu } }));

    const choix = [correct, ...distracteurs];

    const svgUrl = fractionRectsSvg(item.num, item.den);
    const payload = makeQcm(
        choix,
        correct,
        `<p>En t'aidant du dessin, quelle est la décomposition de {{element:0}} en un entier et une fraction inférieure à 1 ?</p>`,
        opts(L5),
    );
    payload.elements_additionnels = {
        version: 1,
        elements: [
            exprElement(fracKatex(item.num, item.den)),
            { type: 'image', config: { imageUrl: svgUrl } },
        ],
    };
    return payload;
};

// ═══════════════════════════════════════════════════════════
// EXERCICE 6 : Calculer avec fractions décimales → 3 écritures
// ═══════════════════════════════════════════════════════════

/** Texte à trous saisie — calcul → fraction → décimal → % (résultat ≤ 100%) */
const genCalcul3EcrituresSaisie = () => {
    // Choisir un dénominateur commun
    const den = randomChoice([100, 1000]);

    // Générer un calcul dont le résultat ≤ 1 (pour que le % ≤ 100)
    let a, b, resultat;
    const isAddition = Math.random() < 0.6;

    if (isAddition) {
        resultat = randInt(10, den <= 100 ? 100 : 1000);
        a = randInt(1, resultat - 1);
        b = resultat - a;
    } else {
        // Soustraction
        a = randInt(20, den <= 100 ? 100 : 1000);
        b = randInt(1, a - 1);
        resultat = a - b;
    }

    // S'assurer que le pourcentage est ≤ 100
    if (resultat / den > 1) {
        // Recalculer pour rester ≤ 1
        resultat = randInt(1, den);
        a = randInt(1, resultat - 1 > 0 ? resultat - 1 : 1);
        b = resultat - a;
    }

    const decResult = (resultat / den).toString().replace('.', ',');
    const pctResult = (resultat / den * 100);
    // Formater proprement le pourcentage
    const pctStr = pctResult === Math.floor(pctResult) ? String(pctResult) : pctResult.toFixed(1).replace('.', ',');

    const op = isAddition ? '+' : '-';
    // Ligne KaTeX globale : le calcul en fractions verticales, le résultat en
    // trou dans le numérateur, puis le décimal et le pourcentage en trous.
    const phrase = `$\\dfrac{${a}}{${den}} ${op} \\dfrac{${b}}{${den}} = ${fracTrouNum(resultat, den)} = {{${decResult}}} = {{${pctStr}}}\\,\\%$`;

    return makeTexteTrou(
        phrase,
        `<p>Donne le résultat sous forme d'une fraction décimale, puis d'une écriture décimale, puis d'un pourcentage.</p>`,
        { ...opts(L6), mode: 'saisie' },
    );
};

/** Tableau V/F — conversions entre écritures */
const genEcrituresVF = () => {
    const lignes = [];
    const reponses = [];

    // Nombre mixte vrai
    const mx1 = randomChoice(FRACTIONS_MIXTES_GENERAL);
    lignes.push(tex(`\\dfrac{${mx1.num}}{${mx1.den}} = ${mx1.entier} + \\dfrac{${mx1.resteNum}}{${mx1.resteDen}}`));
    reponses.push(0); // Vrai

    // Nombre mixte faux (piège : numérateur au lieu de quotient)
    const mx2 = randomChoice(FRACTIONS_MIXTES_GENERAL.filter(m => m !== mx1));
    lignes.push(tex(`\\dfrac{${mx2.num}}{${mx2.den}} = ${mx2.num} + \\dfrac{1}{${mx2.den}}`));
    reponses.push(1); // Faux

    // Pourcentage vrai
    const e1 = randomChoice(ECRITURES.filter(e => e.pct <= 100 && e.pct === Math.floor(e.pct)));
    lignes.push(`${e1.pct} % = ${e1.dec}`);
    reponses.push(0); // Vrai

    // Fraction → décimal faux (erreur de rang : ajoute un 0 juste après la virgule,
    // ce qui divise la valeur par 10). Ex: 50/100 = 0,5 → faux "0,05"
    const e2 = randomChoice(ECRITURES.filter(e => e.den >= 100));
    const fauxDec = e2.dec.replace(',', ',0');
    lignes.push(tex(`\\dfrac{${e2.num}}{${e2.den}} = ${decTex(fauxDec)}`));
    reponses.push(1); // Faux

    // Pourcentage = fraction vrai
    const e3 = randomChoice(ECRITURES.filter(e => e.pct <= 100 && e.den <= 10));
    lignes.push(tex(`\\dfrac{${e3.num}}{${e3.den}} = ${e3.pct}\\,\\%`));
    reponses.push(0); // Vrai

    return makeVraiFaux(
        lignes,
        reponses,
        '<p>Indique si chaque égalité est vraie ou fausse.</p>{{element:0}}',
        opts(L4),
    );
};

/** Relier — 4 écritures d'un même nombre */
const genRelierEcritures = () => {
    // Items à ÉCRITURES DÉCIMALES distinctes : deux fractions de même valeur
    // (ex. 1/5 et 2/10 → "0,2") donneraient deux cibles identiques à relier
    const candidats = shuffle(
        ECRITURES.filter(e => e.pct <= 100 && e.pct === Math.floor(e.pct) && e.den <= 20),
    );
    const items = [];
    const decimauxVus = new Set();
    for (const e of candidats) {
        if (!decimauxVus.has(e.dec)) {
            decimauxVus.add(e.dec);
            items.push(e);
        }
        if (items.length === 4) break;
    }

    // Relier fraction → décimal
    const pairs = items.map(item => ({
        source: fracTex(item.num, item.den),
        target: item.dec,
    }));

    return makeRelier(
        pairs,
        '<p>Relie chaque fraction à son écriture décimale.</p>',
        opts(L4),
    );
};

// ─── Registre ───
const GENERATORS = {
    classer_pct_classer: genClasserParPct,
    intrus_qcm_unique: genIntrusQcm,
    intrus_qcm_multiple: genEcrituresEgalesQcmMulti,
    nombre_mixte_texte_trou_saisie: genNombreMixteSaisie,
    nombre_mixte_qcm_unique: genNombreMixteQcm,
    calcul_3ecritures_texte_trou_saisie: genCalcul3EcrituresSaisie,
    ecritures_tableau_vf: genEcrituresVF,
    ecritures_relier: genRelierEcritures,
};

// ─── Catégories (obligatoire) ───
const FORMAT_LABELS = {
    qcm_unique: 'QCM', qcm_multiple: 'QCM Multi', texte: 'Saisie',
    texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
    relier: 'Relier', tableau_vf: 'Vrai/Faux', classer: 'Classer', ordonner: 'Ordonner',
};

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'classer_pct', label: L3, formatsList: buildFormats('classer_pct') },
    { id: 'intrus', label: L4, formatsList: buildFormats('intrus') },
    { id: 'nombre_mixte', label: L5, formatsList: buildFormats('nombre_mixte') },
    { id: 'calcul_3ecritures', label: L6, formatsList: buildFormats('calcul_3ecritures') },
    { id: 'ecritures', label: L4, formatsList: buildFormats('ecritures') },
];

// ─── Point d'entrée (obligatoire) ───
export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
